import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ApiError } from "@finora/api-client";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";
import { useAuthStore } from "../../store/auth-store";

/**
 * Klik na link iz "Potvrda Finora naloga" mejla - vidi razgovor. Ovde se TEK
 * postavlja lozinka (signup je tražio samo email+ime). Nakon uspeha, backend
 * vraća JWT sa hasCompany=false - korisnik nastavlja pravo na onboarding
 * "Kompanija" korak koji mu tek pravi firmu.
 */
export function ConfirmAccountPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const login = useAuthStore((s) => s.login);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const mutation = useMutation({
    mutationFn: () => api.confirmAccount({ token, password }),
    onSuccess: (data) => {
      login(data.accessToken, data.user, data.companyName, data.hasCompany);
      navigate("/onboarding", { replace: true });
    },
  });

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app-bg px-4 py-10">
        <div className="w-full max-w-md rounded-xl border border-border bg-surface p-8 text-center shadow-subtle">
          <p className="text-sm text-red-600">Link za potvrdu naloga nije ispravan - nedostaje token.</p>
        </div>
      </div>
    );
  }

  const passwordsMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  return (
    <div className="flex min-h-screen items-center justify-center bg-app-bg px-4 py-10">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!passwordsMismatch) mutation.mutate();
        }}
        className="w-full max-w-md rounded-xl border border-border bg-surface p-6 shadow-subtle"
      >
        <h1 className="mb-1 text-xl font-semibold text-slate-900">Postavite lozinku</h1>
        <p className="mb-6 text-sm text-slate-500">Poslednji korak pre nego što podesite svoju firmu.</p>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Lozinka</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Bar 6 karaktera"
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              required
              minLength={6}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Ponovite lozinku</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              required
              minLength={6}
            />
            {passwordsMismatch && <p className="mt-1 text-xs text-red-600">Lozinke se ne poklapaju.</p>}
          </div>

          {mutation.isError && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {mutation.error instanceof ApiError
                ? mutation.error.response.message
                : "Potvrda naloga nije uspela. Pokušajte ponovo."}
            </p>
          )}

          <Button type="submit" className="w-full" disabled={mutation.isPending || passwordsMismatch}>
            {mutation.isPending ? "Potvrđujem..." : "Potvrdi nalog"}
          </Button>

          <p className="text-center text-sm text-slate-500">
            <Link to="/prijava" className="text-brand hover:underline">
              Nazad na prijavu
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
}
